/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { init } from "echarts";
import { GTSLib } from "../../utils/gts.lib";
import { ColorLib } from "../../utils/color-lib";
import { Utils } from "../../utils/utils";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { DataModel } from "../../model/types";
import "moment/min/locales.js";
import { isEqual, throttle } from "lodash";
import { v4 } from "uuid";
export class DiscoveryLineComponent {
    constructor() {
        this.defOptions = Object.assign(Object.assign({}, new Param()), { timeMode: 'date', xCursor: true, yCursor: false });
        this.divider = 1000;
        this.leftMargin = 0;
        this.hasFocus = false;
        this.pois = [];
        this.result = undefined;
        this.type = undefined;
        this.options = Object.assign(Object.assign({}, new Param()), { timeMode: 'date' });
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.unit = '';
        this.parsing = false;
        this.rendering = false;
        this.innerOptions = undefined;
    }
    updateType(newValue, oldValue) {
        if (newValue !== oldValue) {
            this.chartOpts = this.convert(this.result || new DataModel());
            this.setOpts();
        }
    }
    updateRes(newValue, oldValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
            this.result = GTSLib.getData(this.result);
            this.chartOpts = this.convert(this.result || new DataModel());
            this.setOpts(true);
        }
    }
    optionsUpdate(newValue, oldValue) {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], newValue, oldValue);
        let opts = newValue;
        if (!!newValue && typeof newValue === 'string') {
            opts = JSON.parse(newValue);
        }
        if (!isEqual(opts, this.innerOptions)) {
            this.innerOptions = Object.assign({}, opts);
            if (!!this.myChart) {
                this.chartOpts = this.convert(this.result || new DataModel());
                this.setOpts(true);
            }
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['optionsUpdate 2'], { options: this.innerOptions, newValue, oldValue }, this.chartOpts);
        }
    }
    // noinspection JSUnusedGlobalSymbols
    componentWillLoad() {
        var _a;
        this.parsing = true;
        this.LOG = new Logger(DiscoveryLineComponent, this.debug);
        if (typeof this.options === 'string') {
            this.innerOptions = JSON.parse(this.options);
        }
        else {
            this.innerOptions = this.options;
        }
        this.result = GTSLib.getData(this.result);
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], { type: this.type, options: this.innerOptions });
        this.divider = GTSLib.getDivider(this.innerOptions.timeUnit || 'us');
        this.chartOpts = this.convert(this.result || new DataModel());
        this.setOpts();
    }
    setOpts(notMerge = false) {
        if (this.chartOpts.series.length === 0) {
            this.chartOpts.title = {
                show: true,
                textStyle: { color: Utils.getLabelColor(this.el), fontSize: 20 },
                text: this.innerOptions.noDataLabel || '',
                left: 'center',
                top: 'center',
            };
            this.chartOpts.xAxis = { show: false };
            this.chartOpts.yAxis = { show: false };
            this.chartOpts.dataZoom = { show: false };
            this.chartOpts.tooltip = { show: false };
        }
        else {
            this.chartOpts.title = Object.assign(Object.assign({}, this.chartOpts.title || {}), { show: false });
        }
        setTimeout(() => {
            if (this.myChart) {
                this.myChart.setOption(this.chartOpts || {}, notMerge, true);
            }
        });
    }
    convert(data) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2;
        let options = Utils.mergeDeep(this.defOptions, this.innerOptions || {});
        options = Utils.mergeDeep(options || {}, data.globalParams);
        this.innerOptions = Object.assign({}, options);
        this.innerOptions.timeMode = this.innerOptions.timeMode || 'date';
        const gtsList = [
            ...GTSLib.flattenGtsIdArray(GTSLib.flatDeep([data.data]), 0).res,
            ...GTSLib.flatDeep([data.data]).filter(g => g.values && g.label),
        ];
        const gtsCount = gtsList.length;
        let multiY = false;
        let multiX = false;
        const opts = Object.assign({ animation: false, grid: {
                left: (!!this.innerOptions.leftMargin && this.innerOptions.leftMargin > this.leftMargin)
                    ? this.innerOptions.leftMargin - this.leftMargin + 10
                    : 10,
                top: 30,
                bottom: (!!this.innerOptions.showLegend ? 30 : 10) + (!!this.innerOptions.showRangeSelector ? 40 : 0),
                right: 10,
                containLabel: true,
            }, responsive: true, throttle: 40, tooltip: {
                transitionDuration: 0,
                trigger: 'axis',
                animation: false,
                snap: false,
                position: (pos, _params, _dom, _rect, size) => {
                    const obj = { top: 10 };
                    obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 5;
                    return obj;
                },
                formatter: (params) => `<div style="font-size:14px;color:#666;font-weight:400;line-height:1;">${this.innerOptions.timeMode !== 'date'
                    ? params[0].value[0]
                    : (GTSLib.toISOString(GTSLib.zonedTimeToUtc(params[0].value[0], 1, this.innerOptions.timeZone), 1, this.innerOptions.timeZone, this.innerOptions.fullDateDisplay ? this.innerOptions.timeFormat : undefined) || '')
                        .replace('T', ' ').replace(/\+[0-9]{2}:[0-9]{2}$/gi, '')}</div>
               ${params.map(s => `${s.marker} <span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">${GTSLib.getName(s.seriesName)}</span>
            <span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">${s.value[1]}</span>`).join('<br>')}`,
                axisPointer: {
                    type: !!this.innerOptions.yCursor && !!this.innerOptions.xCursor ? 'cross' : !!this.innerOptions.yCursor || !!this.innerOptions.xCursor ? 'line' : 'none',
                    axis: !!this.innerOptions.yAxisFocus ? 'y' : 'x',
                    animation: false,
                    lineStyle: !this.innerOptions.yCursor && !this.innerOptions.xCursor
                        ? undefined
                        : {
                            color: Utils.getCSSColor(this.el, '--warp-view-bar-color', 'red'),
                        },
                    crossStyle: !!this.innerOptions.yCursor
                        ? {
                            color: Utils.getCSSColor(this.el, '--warp-view-bar-color', 'red'),
                        }
                        : undefined,
                },
                backgroundColor: Utils.getCSSColor(this.el, '--warp-view-tooltip-bg-color', 'white'),
                hideDelay: (_a = this.innerOptions.tooltipDelay) !== null && _a !== void 0 ? _a : 100,
            }, toolbox: {
                show: this.innerOptions.showControls,
                feature: {
                    saveAsImage: { type: 'png', excludeComponents: ['toolbox'] },
                    restore: { show: true },
                },
            }, legend: {
                bottom: 0, left: 'center', show: !!this.innerOptions.showLegend, height: 30, type: 'scroll',
                textStyle: { color: Utils.getLabelColor(this.el) },
                formatter: n => GTSLib.getName(n),
            }, dataZoom: [
                {
                    type: 'inside',
                    realtime: true,
                    filterMode: 'none',
                    orient: 'horizontal',
                    zoomOnMouseWheel: true,
                },
                {
                    type: 'slider',
                    height: '20px',
                    show: !!this.innerOptions.showRangeSelector,
                    bottom: !!this.innerOptions.showLegend ? 30 : 20,
                    filterMode: 'none',
                },
                {
                    type: 'inside',
                    realtime: true,
                    filterMode: 'none',
                    orient: 'vertical',
                    zoomOnMouseWheel: 'ctrl',
                },
            ], visualMap: new Array(gtsCount), series: [] }, ((_c = (_b = this.innerOptions) === null || _b === void 0 ? void 0 : _b.extra) === null || _c === void 0 ? void 0 : _c.chartOpts) || {});
        let min = Number.MAX_SAFE_INTEGER;
        let max = Number.MIN_SAFE_INTEGER;
        let hasTimeBounds = false;
        for (let index = 0; index < gtsCount; index++) {
            const gts = gtsList[index];
            const datasetNoAlpha = (_f = (_e = ((_d = data.params) !== null && _d !== void 0 ? _d : [])[index]) === null || _e === void 0 ? void 0 : _e.datasetNoAlpha) !== null && _f !== void 0 ? _f : this.innerOptions.datasetNoAlpha;
            if (GTSLib.isGtsToPlot(gts)) {
                const c = ColorLib.getColor(gts.id, this.innerOptions.scheme);
                const color = ((data.params || [])[gts.id] || { datasetColor: c }).datasetColor || c;
                const type = ((data.params || [])[gts.id] || { type: this.type }).type || this.type;
                if (!!data.params && !!data.params[gts.id] && (data.params[gts.id].pieces || []).length > 0) {
                    opts.visualMap[gts.id] = {
                        show: false,
                        seriesIndex: gts.id,
                        dimension: !!data.params[gts.id].xpieces ? 0 : 1,
                        pieces: data.params[gts.id].pieces.map(p => ({ color: p.color || '#D81B60', lte: p.lte, gte: p.gte })),
                        outOfRange: { color },
                    };
                }
                hasTimeBounds = true;
                const dataSet = [];
                for (let v = 0; v < ((_g = gts.v) !== null && _g !== void 0 ? _g : []).length; v++) {
                    const tuple = gts.v[v];
                    const ts = tuple[0];
                    const val = tuple[tuple.length - 1];
                    if (ts > max)
                        max = ts;
                    if (ts < min)
                        min = ts;
                    dataSet.push([
                        this.innerOptions.timeMode === 'date'
                            ? GTSLib.utcToZonedTime(ts, this.divider, this.innerOptions.timeZone)
                            : ts,
                        val,
                    ]);
                }
                const isStacked = ((_j = ((_h = data.params) !== null && _h !== void 0 ? _h : [])[gts.id]) === null || _j === void 0 ? void 0 : _j.stacked) !== undefined
                    ? (_l = ((_k = data.params) !== null && _k !== void 0 ? _k : [])[gts.id]) === null || _l === void 0 ? void 0 : _l.stacked
                    : (_m = this.innerOptions) === null || _m === void 0 ? void 0 : _m.stacked;
                const s = {
                    type: type === 'scatter' || gts.v.length <= 1 ? 'scatter' : ['scatter', 'line', 'bar'].includes(type) ? type : 'line',
                    name: GTSLib.setName(gts.id, ((_q = ((_p = ((_o = data.params) !== null && _o !== void 0 ? _o : [])[gts.id]) !== null && _p !== void 0 ? _p : { key: undefined }).key) !== null && _q !== void 0 ? _q : GTSLib.serializeGtsMetadata(gts))),
                    data: dataSet,
                    id: gts.id,
                    animation: false,
                    large: true,
                    showSymbol: this.type === 'scatter' || this.innerOptions.showDots || this.innerOptions.showValues,
                    symbolSize: (_r = this.innerOptions.dotSize) !== null && _r !== void 0 ? _r : 10,
                    smooth: type === 'spline' || type === 'spline-area' ? 0.2 : undefined,
                    clip: true,
                    stack: 'scatter' !== type && isStacked ? 'total' : undefined,
                    stackStrategy: 'scatter' !== type && isStacked ? 'all' : undefined,
                    step: DiscoveryLineComponent.getStepShape(type),
                    areaStyle: type === 'area' || type === 'step-area' || type === 'spline-area' ? {
                        opacity: 0.8,
                        color: {
                            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                            colorStops: [
                                { offset: 0, color: ColorLib.transparentize(color, datasetNoAlpha ? 1 : 0.7) },
                                { offset: 1, color: ColorLib.transparentize(color, datasetNoAlpha ? 1 : 0.1) },
                            ],
                            global: false, // false by default
                        },
                    } : undefined,
                    showAllSymbol: false,
                    label: {
                        show: !!this.innerOptions.showValues,
                        position: 'top',
                        textStyle: { color: Utils.getLabelColor(this.el), fontSize: 14 },
                    },
                    lineStyle: {
                        color: !opts.visualMap[gts.id] ? color : undefined,
                        width: (_s = this.innerOptions.strokeWidth) !== null && _s !== void 0 ? _s : 2,
                    },
                    itemStyle: type === 'bar' ? {
                        opacity: 0.8,
                        borderColor: color,
                        color: {
                            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                            colorStops: [
                                { offset: 0, color: ColorLib.transparentize(color, datasetNoAlpha ? 1 : 0.7) },
                                { offset: 1, color: ColorLib.transparentize(color, datasetNoAlpha ? 1 : 0.3) },
                            ],
                        },
                    } : { color },
                };
                if (!!data.params) {
                    // multi Y
                    if (((_t = data.params[gts.id]) === null || _t === void 0 ? void 0 : _t.yAxis) !== undefined) {
                        multiY = true;
                        const y = this.getYAxis(color, data.params[gts.id].unit);
                        if (data.params[gts.id].yAxis > 0) {
                            s.yAxisIndex = data.params[gts.id].yAxis;
                            y.position = 'right';
                            if (!opts.yAxis)
                                opts.yAxis = new Array(data.params.length);
                            opts.yAxis[data.params[gts.id].yAxis] = y;
                        }
                        else {
                            y.position = 'left';
                            if (!opts.yAxis)
                                opts.yAxis = new Array(data.params.length);
                            opts.yAxis[0] = y;
                        }
                    }
                    else if (multiY) {
                        const y = this.getYAxis(undefined, data.params[gts.id].unit);
                        y.position = 'left';
                        if (!opts.yAxis)
                            opts.yAxis = new Array(data.params.length);
                        opts.yAxis[0] = y;
                    }
                    // multi X
                    if (((_u = data.params[gts.id]) === null || _u === void 0 ? void 0 : _u.xAxis) !== undefined) {
                        multiX = true;
                        if (data.params[gts.id].xAxis > 0) {
                            s.xAxisIndex = data.params[gts.id].xAxis;
                            const x = this.getXAxis(color);
                            x.position = 'top';
                            if (!opts.xAxis)
                                opts.xAxis = new Array(data.params.length);
                            opts.xAxis[data.params[gts.id].xAxis] = x;
                        }
                        else {
                            const x = this.getXAxis(color);
                            x.position = 'bottom';
                            if (!opts.xAxis)
                                opts.xAxis = new Array(data.params.length);
                            opts.xAxis[0] = x;
                        }
                    }
                    else if (multiX) {
                        const x = this.getXAxis();
                        x.position = 'bottom';
                        if (!opts.xAxis)
                            opts.xAxis = new Array(data.params.length);
                        opts.xAxis[0] = x;
                    }
                }
                opts.series.push(s);
            }
            else if (['scatter', 'line'].includes(this.type) && gts.label && gts.values) {
                // Custom data for scatter
                this.innerOptions.timeMode = 'custom';
                const id = (_v = gts === null || gts === void 0 ? void 0 : gts.id) !== null && _v !== void 0 ? _v : index;
                const color = (_y = (_x = ((_w = data.params) !== null && _w !== void 0 ? _w : [])[id]) === null || _x === void 0 ? void 0 : _x.datasetColor) !== null && _y !== void 0 ? _y : ColorLib.getColor(id, this.innerOptions.scheme);
                const sMax = Math.max(...gts.values.map((l) => l[2] || 1)) || 1;
                const sMin = Math.min(...gts.values.map((l) => l[2] || 0)) || 0;
                const isBubble = sMax !== sMin;
                const s = {
                    type: this.type,
                    name: GTSLib.setName(gts.id, gts.label),
                    id: gts.id,
                    data: gts.values[0] && gts.values[0].length === 3
                        ? ((_z = gts.values) !== null && _z !== void 0 ? _z : []).map((v) => ({
                            value: [GTSLib.utcToZonedTime(v[0], 1, this.innerOptions.timeZone), v[1]],
                            symbolSize: isBubble ? (50 * v[2] / (sMax - sMin)) || this.innerOptions.dotSize || 10 : this.innerOptions.dotSize || 10,
                        }))
                        : gts.values,
                    animation: false,
                    large: true,
                    showSymbol: true,
                    label: {
                        show: !!this.innerOptions.showValues,
                        position: 'top',
                        textStyle: { color: Utils.getLabelColor(this.el), fontSize: 14 },
                    },
                    itemStyle: isBubble || this.innerOptions.dotSize > 10 ? {
                        opacity: 0.8,
                        borderColor: color,
                        borderWidth: this.innerOptions.strokeWidth,
                        color: gts.values[0] && gts.values[0].length === 3
                            ? {
                                type: 'radial', x: 0.5, y: 0.5, x2: 1, y2: 1,
                                colorStops: [
                                    { offset: 0, color: ColorLib.transparentize(color, datasetNoAlpha ? 1 : 0.3) },
                                    { offset: 1, color: ColorLib.transparentize(color, datasetNoAlpha ? 1 : 0.7) },
                                ],
                            }
                            : color,
                    } : { color },
                };
                if (!!data.params) {
                    // multi Y
                    if (((_0 = data.params[gts.id]) === null || _0 === void 0 ? void 0 : _0.yAxis) !== undefined) {
                        multiY = true;
                        const y = this.getYAxis(color, data.params[gts.id].unit);
                        if (data.params[gts.id].yAxis > 0) {
                            s.yAxisIndex = data.params[gts.id].yAxis;
                            y.position = 'right';
                            if (!opts.yAxis)
                                opts.yAxis = new Array(data.params.length);
                            opts.yAxis[data.params[gts.id].yAxis] = y;
                        }
                        else {
                            y.position = 'left';
                            if (!opts.yAxis)
                                opts.yAxis = new Array(data.params.length);
                            opts.yAxis[0] = y;
                        }
                    }
                    else if (multiY) {
                        const y = this.getYAxis(undefined, data.params[gts.id].unit);
                        y.position = 'left';
                        if (!opts.yAxis)
                            opts.yAxis = new Array(data.params.length);
                        opts.yAxis[0] = y;
                    }
                    // multi X
                    if (((_1 = data.params[gts.id]) === null || _1 === void 0 ? void 0 : _1.xAxis) !== undefined) {
                        multiX = true;
                        if (data.data[gts.id].length > 0) {
                            if (data.params[gts.id].xAxis > 0) {
                                s.xAxisIndex = data.params[gts.id].xAxis;
                                const x = this.getXAxis(color);
                                x.position = 'top';
                                if (!opts.xAxis)
                                    opts.xAxis = new Array(data.params.length);
                                opts.xAxis[data.params[gts.id].xAxis] = x;
                            }
                            else {
                                const x = this.getXAxis(color);
                                x.position = 'bottom';
                                if (!opts.xAxis)
                                    opts.xAxis = new Array(data.params.length);
                                opts.xAxis[0] = x;
                            }
                        }
                    }
                    else if (multiX) {
                        if (data.params[gts.id].xAxis > 0) {
                            const x = this.getXAxis();
                            x.position = 'bottom';
                            if (!opts.xAxis)
                                opts.xAxis = new Array(data.params.length);
                            opts.xAxis[0] = x;
                        }
                    }
                }
                opts.series.push(s);
            }
        }
        (this.innerOptions.polygons || []).forEach((polygon, i) => {
            const s = {
                type: 'custom', renderItem: (params, api) => {
                    if (params.context.rendered) {
                        return;
                    }
                    params.context.rendered = true;
                    const color = polygon.color || ColorLib.getColor(i, this.innerOptions.scheme);
                    return {
                        type: 'polygon',
                        transition: ['shape'],
                        shape: { points: polygon.shape.map(p => api.coord([
                                this.innerOptions.timeMode === 'date'
                                    ? GTSLib.utcToZonedTime(p[0], this.divider, this.innerOptions.timeZone)
                                    : p[0], p[1]
                            ])) },
                        style: api.style({
                            fill: !!polygon.fill ? ColorLib.transparentize(color) : undefined,
                            stroke: color,
                        }),
                    };
                },
                clip: true,
                data: polygon.shape,
            };
            opts.series.push(s);
        });
        if (hasTimeBounds) {
            this.timeBounds.emit({ min, max });
            this.bounds = { min, max };
        }
        // multi Y
        if (!multiY) {
            opts.yAxis = this.getYAxis();
        }
        else {
            const yAxis = [...GTSLib.cleanArray(opts.yAxis)];
            opts.yAxis = [];
            let i = 0;
            yAxis.forEach((y) => {
                if (y.position === 'right') {
                    y.offset = 80 * i;
                    i++;
                }
                opts.yAxis.push(y);
            });
            opts.grid.right = 80 * (i - 1);
        }
        // multi X
        if (!multiX) {
            opts.xAxis = this.getXAxis();
        }
        else {
            const xAxis = [...GTSLib.cleanArray(opts.xAxis)];
            opts.xAxis = [];
            let i = 0;
            xAxis.forEach((x) => {
                if (x.position === 'top') {
                    x.offset = 30 * (i + 1);
                    i++;
                }
                opts.xAxis.push(x);
            });
            opts.grid.top = Math.max(30, 30 * i);
        }
        (_2 = this.LOG) === null || _2 === void 0 ? void 0 : _2.debug(['convert', 'opts'], { opts });
        const markArea = [...(this.innerOptions.thresholds || [])
                .filter(t => !!t.fill)
                .map(t => {
                return [{
                        itemStyle: {
                            color: ColorLib.transparentize(t.color || '#D81B60', !!t.fill ? 0.5 : 0),
                            borderType: t.type || 'dashed',
                            name: t.name || t.value || 0,
                        },
                        yAxis: t.value || 0,
                    }, {
                        itemStyle: t.from ? {
                            color: ColorLib.transparentize(t.color || '#D81B60', !!t.fill ? 0.5 : 0),
                            borderType: t.type || 'dashed',
                            name: t.name || t.value || 0,
                        } : undefined,
                        yAxis: t.from || 0,
                    }];
            }),
            ...(this.innerOptions.markers || [])
                .filter(t => !!t.fill)
                .map(t => {
                return [{
                        itemStyle: {
                            color: ColorLib.transparentize(t.color || '#D81B60', !!t.fill ? t.alpha || 0.5 : 0),
                            borderType: t.type || 'dashed',
                        },
                        label: { color: t.color || '#D81B60', position: 'insideTop', distance: 5, show: !!t.name },
                        name: t.name || t.value || 0,
                        xAxis: ((t.value / (this.innerOptions.timeMode === 'date' ? this.divider : 1)) || 0),
                    },
                    {
                        itemStyle: {
                            color: ColorLib.transparentize(t.color || '#D81B60', !!t.fill ? t.alpha || 0.5 : 0),
                            borderType: t.type || 'dashed',
                        },
                        xAxis: ((t.start / (this.innerOptions.timeMode === 'date' ? this.divider : 1)) || 0),
                    }];
            }),
        ];
        const markLine = [
            ...(this.innerOptions.thresholds || []).map(t => {
                return {
                    name: t.name || t.value || 0,
                    label: { color: t.color || '#D81B60', position: 'insideEndTop', formatter: '{b}' },
                    lineStyle: { color: t.color || '#D81B60', type: t.type || 'dashed' },
                    yAxis: t.value || 0,
                };
            }),
            ...(this.innerOptions.markers || [])
                .filter(t => !t.fill)
                .map((t, i) => {
                return {
                    name: t.name || t.value || 'mark-' + i,
                    label: {
                        color: t.color || '#D81B60',
                        position: 'insideEndTop',
                        formatter: '{b}',
                        show: !!t.name,
                    },
                    lineStyle: { color: t.color || '#D81B60', type: t.type || 'dashed' },
                    xAxis: ((t.value / (this.innerOptions.timeMode === 'date' ? this.divider : 1)) || 0),
                };
            })
        ];
        if (markArea.length > 0 || markLine.length > 0) {
            opts.series.push({
                name: '',
                type: 'line',
                symbolSize: 0,
                data: [],
                markArea: { data: markArea },
                markLine: {
                    emphasis: { lineStyle: { width: 1 } },
                    symbol: ['none', 'none'],
                    data: markLine,
                },
            });
        }
        return opts;
    }
    getYAxis(color, unit) {
        if (!!(unit || this.unit || this.innerOptions.unit) && !!this.myChart) {
            const opts = Object.assign({}, this.chartOpts);
            if (opts.grid) {
                if ('top' in opts.grid) {
                    opts.grid.top = 30;
                }
                setTimeout(() => this.myChart.setOption(opts, true, false));
            }
        }
        return {
            type: this.innerOptions.yLabelsMapping ? 'category' : 'value',
            name: unit || this.unit || this.innerOptions.unit,
            show: !this.innerOptions.hideYAxis,
            nameTextStyle: { color: color || Utils.getLabelColor(this.el) },
            splitLine: { show: false, lineStyle: { color: Utils.getGridColor(this.el) } },
            axisLine: { show: true, lineStyle: { color: color || Utils.getGridColor(this.el) } },
            axisLabel: { color: color || Utils.getLabelColor(this.el), show: !this.innerOptions.hideYAxis },
            axisTick: { show: true, lineStyle: { color: color || Utils.getGridColor(this.el) } },
            data: this.innerOptions.yLabelsMapping ? Object.keys(this.innerOptions.yLabelsMapping).map(k => this.innerOptions.yLabelsMapping[k]) : undefined,
            scale: !(this.innerOptions.bounds && this.innerOptions.bounds.yRanges && this.innerOptions.bounds.yRanges.length > 0),
            min: this.innerOptions.bounds && this.innerOptions.bounds.yRanges && this.innerOptions.bounds.yRanges.length > 0 ? this.innerOptions.bounds.yRanges[0] : undefined,
            max: this.innerOptions.bounds && this.innerOptions.bounds.yRanges && this.innerOptions.bounds.yRanges.length > 0 ? this.innerOptions.bounds.yRanges[1] : undefined,
        };
    }
    getXAxis(color) {
        var _a, _b;
        return {
            type: this.innerOptions.timeMode === 'date' ? 'time' : 'value',
            show: !this.innerOptions.hideXAxis,
            splitNumber: this.innerOptions.timeMode === 'date' ? undefined : Math.max(Math.floor(Utils.getContentBounds(this.el.parentElement).w / 200) - 1, 1),
            splitLine: { show: false, lineStyle: { color: Utils.getGridColor(this.el) } },
            axisLine: { lineStyle: { color: color || Utils.getGridColor(this.el) } },
            axisLabel: {
                show: !this.innerOptions.hideXAxis,
                color: color || Utils.getLabelColor(this.el),
                formatter: this.innerOptions.fullDateDisplay
                    ? (value) => GTSLib.toISOString(GTSLib.zonedTimeToUtc(value, 1, this.innerOptions.timeZone), 1, this.innerOptions.timeZone, this.innerOptions.timeFormat)
                        .replace('T', '\n').replace(/\+[0-9]{2}:[0-9]{2}$/gi, '')
                    : undefined,
            },
            axisTick: { lineStyle: { color: color || Utils.getGridColor(this.el) } },
            scale: !(this.innerOptions.bounds && (!!this.innerOptions.bounds.minDate || !!this.innerOptions.bounds.maxDate)),
            min: ((_a = this.innerOptions.bounds) === null || _a === void 0 ? void 0 : _a.minDate) !== undefined
                ? this.innerOptions.timeMode === 'date'
                    ? GTSLib.utcToZonedTime(this.innerOptions.bounds.minDate, this.divider, this.innerOptions.timeZone)
                    : this.innerOptions.bounds.minDate
                : undefined,
            max: ((_b = this.innerOptions.bounds) === null || _b === void 0 ? void 0 : _b.maxDate) !== undefined
                ? this.innerOptions.timeMode === 'date'
                    ? GTSLib.utcToZonedTime(this.innerOptions.bounds.maxDate, this.divider, this.innerOptions.timeZone)
                    : this.innerOptions.bounds.maxDate
                : undefined,
        };
    }
    static getStepShape(type) {
        switch (type) {
            case 'line':
            case 'area':
            case 'spline':
                return undefined;
            case 'step':
            case 'step-area':
                return 'end';
            case 'step-before':
                return 'start';
            case 'step-after':
                return 'end';
        }
    }
    zoomHandler(start, end) {
        var _a, _b, _c, _d;
        this.dataZoom.emit({
            start,
            end,
            min: ((_a = this.innerOptions.bounds) === null || _a === void 0 ? void 0 : _a.minDate) || ((_b = this.bounds) === null || _b === void 0 ? void 0 : _b.min),
            max: ((_c = this.innerOptions.bounds) === null || _c === void 0 ? void 0 : _c.maxDate) || ((_d = this.bounds) === null || _d === void 0 ? void 0 : _d.max),
        });
    }
    // noinspection JSUnusedGlobalSymbols
    componentDidLoad() {
        const zoomHandler = throttle((start, end) => this.zoomHandler(start, end), 16, { leading: true, trailing: true });
        const focusHandler = throttle((type, event) => {
            if (this.hasFocus) {
                switch (type) {
                    case 'mouseover':
                        const c = event.data.coord || event.data;
                        this.dataPointOver.emit({ date: c[0], name: event.seriesName, value: c[1], meta: {} });
                        break;
                    case 'highlight':
                        let ts;
                        (event.batch || []).forEach((b) => {
                            const s = this.myChart.getOption().series[b.seriesIndex];
                            ts = s.data[b.dataIndex][0];
                            ts = this.innerOptions.timeMode === 'date'
                                ? GTSLib.zonedTimeToUtc(ts * this.divider, this.divider, this.innerOptions.timeZone || 'UTC') * this.divider
                                : ts;
                        });
                        if (ts !== undefined) {
                            this.dataPointOver.emit({ date: ts, name: '.*', meta: {} });
                        }
                        break;
                    default:
                        break;
                }
            }
        }, 200, { leading: true, trailing: true });
        setTimeout(() => {
            this.parsing = false;
            this.rendering = true;
            this.myChart = init(this.graph, null, { renderer: 'canvas' });
            let initial = false;
            this.myChart.on('rendered', () => {
                this.rendering = false;
                let found = false;
                let x = 0;
                setTimeout(() => {
                    while (!found && x < 1024) {
                        found = this.myChart.containPixel({ gridIndex: 0 }, [x, this.myChart.getHeight() / 2]);
                        x++;
                    }
                    if (this.leftMargin !== x && x < 1024) {
                        setTimeout(() => {
                            this.leftMarginComputed.emit(x);
                            this.leftMargin = x;
                        });
                    }
                    if (initial)
                        setTimeout(() => this.draw.emit());
                    initial = false;
                });
            });
            this.myChart.on('dataZoom', (event) => {
                let start;
                let end;
                if (!!event.batch) {
                    const batch = (event.batch || [])[0] || {};
                    start = batch.start || batch.startValue;
                    end = batch.end || batch.endValue;
                    this.zoomHandler(start, end);
                }
                else if (event.start !== undefined && event.end !== undefined) {
                    start = event.start;
                    end = event.end;
                    zoomHandler(start, end);
                }
            });
            this.myChart.on('restore', () => {
                this.dataZoom.emit({ type: 'restore', start: 0, end: 100 });
            });
            this.el.addEventListener('dblclick', () => this.myChart.dispatchAction({
                type: 'dataZoom',
                start: 0,
                end: 100,
            }));
            this.el.addEventListener('mouseover', () => this.hasFocus = true);
            this.el.addEventListener('mouseout', () => {
                this.hasFocus = false;
                this.dataPointOver.emit({});
            });
            this.myChart.on('mouseout', () => {
                this.dataPointOver.emit({});
            });
            this.myChart.on('mouseover', (event) => focusHandler('mouseover', event));
            this.myChart.on('highlight', (event) => focusHandler('highlight', event));
            this.myChart.on('click', (event) => {
                const c = event.data.coord || event.data;
                const date = this.innerOptions.timeMode === 'date'
                    ? GTSLib.zonedTimeToUtc(c[0], 1, this.innerOptions.timeZone) * this.divider
                    : c[0];
                if (event.componentType !== 'markLine') {
                    this.dataPointSelected.emit({ date, name: GTSLib.getName(event.seriesName), value: c[1], meta: {} });
                }
                if (this.innerOptions.poi) {
                    if (this.pois.find(p => p.date === date)) {
                        this.pois = this.pois.filter(p => p.date !== date);
                    }
                    else if (event.componentType !== 'markLine') {
                        this.pois.push({
                            date,
                            name: GTSLib.getName(event.seriesName),
                            value: c[1],
                            meta: {},
                            uid: v4(),
                        });
                    }
                    this.chartOpts.series = this.chartOpts.series.filter(s => 'poi' !== s.id);
                    this.poi.emit(this.pois);
                    this.chartOpts.series.push({
                        name: '',
                        id: 'poi',
                        type: 'line',
                        data: [],
                        markLine: {
                            emphasis: { lineStyle: { width: 1 } },
                            symbol: ['none', 'pin'],
                            symbolSize: 20,
                            symbolKeepAspect: true,
                            data: this.pois.map(p => {
                                return {
                                    name: 'poi-' + p.uid,
                                    label: { show: false },
                                    lineStyle: { color: this.innerOptions.poiColor, type: this.innerOptions.poiLine },
                                    xAxis: this.innerOptions.timeMode === 'date'
                                        ? GTSLib.utcToZonedTime(p.date / this.divider, 1, this.innerOptions.timeZone)
                                        : p.date,
                                };
                            }),
                        },
                    });
                    setTimeout(() => { var _a; return this.myChart.setOption((_a = this.chartOpts) !== null && _a !== void 0 ? _a : {}, true, false); });
                }
            });
            setTimeout(() => { var _a; return this.myChart.setOption((_a = this.chartOpts) !== null && _a !== void 0 ? _a : {}, true, false); });
            initial = true;
        });
    }
    async resize() {
        if (this.myChart) {
            this.myChart.resize();
        }
        return Promise.resolve();
    }
    async setZoom(dataZoom) {
        var _a, _b;
        if (!!this.myChart) {
            dataZoom.start = dataZoom.start || 0;
            if (((_a = this.zoom) === null || _a === void 0 ? void 0 : _a.start) !== dataZoom.start || ((_b = this.zoom) === null || _b === void 0 ? void 0 : _b.end) !== dataZoom.end) {
                this.zoom = dataZoom;
                this.myChart.dispatchAction(Object.assign(Object.assign({ type: 'dataZoom' }, dataZoom), { dataZoomIndex: 0 }));
            }
        }
        return Promise.resolve();
    }
    async export(type = 'png') {
        return Promise.resolve(this.myChart ? this.myChart.getDataURL({
            type,
            excludeComponents: ['toolbox'],
        }) : undefined);
    }
    async show(regexp) {
        this.myChart.dispatchAction({
            type: 'legendSelect',
            batch: this.myChart.getOption().series
                .filter(s => new RegExp(regexp).test(GTSLib.getName(s.name))),
        });
        return Promise.resolve();
    }
    async hide(regexp) {
        this.myChart.dispatchAction({
            type: 'legendUnSelect',
            batch: this.myChart.getOption().series
                .filter(s => new RegExp(regexp).test(GTSLib.getName(s.name))),
        });
        return Promise.resolve();
    }
    async hideById(id) {
        if (this.myChart) {
            this.myChart.dispatchAction({
                type: 'legendUnSelect',
                batch: this.myChart.getOption().series
                    .filter((s, i) => new RegExp(id.toString()).test((s.id || i).toString())),
            });
        }
        return Promise.resolve();
    }
    async showById(id) {
        if (this.myChart) {
            this.myChart.dispatchAction({
                type: 'legendSelect',
                batch: this.myChart.getOption().series
                    .filter((s, i) => new RegExp(id.toString()).test((s.id || i).toString())),
            });
        }
        return Promise.resolve();
    }
    async setFocus(regexp, ts, value) {
        if (!this.myChart || this.hasFocus)
            return;
        const date = this.innerOptions.timeMode === 'date'
            ? GTSLib.utcToZonedTime(ts || 0, this.divider, this.innerOptions.timeZone)
            : ts || 0;
        let seriesIndex = 0;
        let dataIndex = 0;
        if (!!regexp) {
            this.chartOpts.series
                .filter(s => new RegExp(regexp).test(s.name))
                .forEach(s => {
                const data = s.data.filter((d) => d[0] === date);
                if (data && data.length > 0 && data[0]) {
                    seriesIndex = this.chartOpts.series.indexOf(s);
                    dataIndex = s.data.indexOf(data[0]);
                    s.markPoint = {
                        symbol: 'circle', data: [{
                                symbolSize: 5,
                                name: s.name,
                                itemStyle: { color: '#fff', borderColor: s.lineStyle.color },
                                yAxis: data[0][1],
                                xAxis: date,
                            }],
                    };
                }
            });
        }
        if (GTSLib.isArray(this.chartOpts.xAxis)) {
            this.chartOpts.xAxis
                .forEach(a => a.axisPointer = Object.assign(Object.assign({}, a.axisPointer || {}), { value: date, status: 'show' }));
        }
        else {
            this.chartOpts.xAxis.axisPointer = Object.assign(Object.assign({}, this.chartOpts.xAxis.axisPointer || {}), { value: date, status: 'show' });
        }
        if (GTSLib.isArray(this.chartOpts.yAxis)) {
            this.chartOpts.yAxis
                .forEach(a => a.axisPointer = Object.assign(Object.assign({}, a.axisPointer || {}), { value: value || 0, status: 'show' }));
        }
        else {
            this.chartOpts.yAxis.axisPointer = Object.assign(Object.assign({}, this.chartOpts.yAxis.axisPointer || {}), { value: value || 0, status: 'show' });
        }
        this.chartOpts.tooltip.show = true;
        this.myChart.dispatchAction({ type: 'showTip', seriesIndex, dataIndex });
        this.setOpts();
        return Promise.resolve();
    }
    async unFocus() {
        if (!this.myChart || this.hasFocus)
            return;
        this.chartOpts.series.forEach(s => s.markPoint = undefined);
        if (GTSLib.isArray(this.chartOpts.xAxis)) {
            this.chartOpts.xAxis
                .forEach(a => a.axisPointer = Object.assign(Object.assign({}, a.axisPointer || {}), { status: 'hide' }));
        }
        else {
            this.chartOpts.xAxis.axisPointer = Object.assign(Object.assign({}, this.chartOpts.xAxis.axisPointer || {}), { status: 'hide' });
        }
        if (GTSLib.isArray(this.chartOpts.yAxis)) {
            this.chartOpts.yAxis
                .forEach(a => a.axisPointer = Object.assign(Object.assign({}, a.axisPointer || {}), { status: 'hide' }));
        }
        else {
            this.chartOpts.yAxis.axisPointer = Object.assign(Object.assign({}, this.chartOpts.yAxis.axisPointer || {}), { status: 'hide' });
        }
        this.myChart.dispatchAction({ type: 'hideTip' });
        this.setOpts();
        return Promise.resolve();
    }
    hideMarkers() {
        var _a, _b, _c, _d;
        if (!!this.myChart && ((_b = (_a = this.chartOpts) === null || _a === void 0 ? void 0 : _a.series) !== null && _b !== void 0 ? _b : []).some(s => !!s.markPoint)) {
            ((_d = (_c = this.chartOpts) === null || _c === void 0 ? void 0 : _c.series) !== null && _d !== void 0 ? _d : []).forEach(s => s.markPoint = undefined);
            this.setOpts();
        }
    }
    render() {
        var _a;
        return h("div", { key: 'c5e09f54ca82c21fb6fc954a5ee5c8f129975eb5', style: { width: '100%', height: '100%' } }, this.parsing && !!((_a = this.innerOptions) === null || _a === void 0 ? void 0 : _a.showLoader) ?
            h("discovery-spinner", null, "Parsing data...") : '', this.rendering ? h("discovery-spinner", null, "Rendering data...") : '', h("div", { key: 'f490741fed32be9e72d728f1559e0da6466fb714', ref: (el) => this.graph = el, onMouseOver: () => this.hideMarkers() }));
    }
    static get is() { return "discovery-line"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-line.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-line.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "{ ...new Param(), timeMode: 'date' }"
            },
            "width": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "unit",
                "reflect": false,
                "defaultValue": "''"
            }
        };
    }
    static get states() {
        return {
            "width": {},
            "height": {},
            "parsing": {},
            "rendering": {},
            "innerOptions": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "dataZoom",
                "name": "dataZoom",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "{ start?: number, end?: number, min?: number, max?: number, type?: string }",
                    "resolved": "{ start?: number; end?: number; min?: number; max?: number; type?: string; }",
                    "references": {}
                }
            }, {
                "method": "leftMarginComputed",
                "name": "leftMarginComputed",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }, {
                "method": "dataPointOver",
                "name": "dataPointOver",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "dataPointSelected",
                "name": "dataPointSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "poi",
                "name": "poi",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "timeBounds",
                "name": "timeBounds",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "setZoom": {
                "complexType": {
                    "signature": "(dataZoom: { start?: number; end?: number; }) => Promise<void>",
                    "parameters": [{
                            "name": "dataZoom",
                            "type": "{ start?: number; end?: number; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: 'png' | 'svg') => Promise<string>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "show": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hide": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hideById": {
                "complexType": {
                    "signature": "(id: number | string) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "string | number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "showById": {
                "complexType": {
                    "signature": "(id: number | string) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "string | number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "setFocus": {
                "complexType": {
                    "signature": "(regexp: string, ts: number, value?: number) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "ts",
                            "type": "number",
                            "docs": ""
                        }, {
                            "name": "value",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "unFocus": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "type",
                "methodName": "updateType"
            }, {
                "propName": "result",
                "methodName": "updateRes"
            }, {
                "propName": "options",
                "methodName": "optionsUpdate"
            }];
    }
}
//# sourceMappingURL=discovery-line.js.map
